import React from 'react';
import { Link } from 'react-router-dom';
import { Target, CheckCircle, TrendingUp, Brain, Clock, Layers, Users, Calendar, Zap, BarChart } from 'lucide-react';

function HomePage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex flex-col">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:justify-between py-4 md:h-16 md:py-0">
            <div className="flex justify-center md:justify-start items-center">
              <Target className="h-8 w-8 text-indigo-600" />
              <span className="ml-2 text-2xl font-bold text-gray-900">Goalie4</span>
            </div>
            <div className="flex justify-center items-center mt-4 md:mt-0 space-x-4">
              <Link 
                to="/login" 
                className="text-gray-700 hover:text-gray-900 px-4 py-2 font-medium transition-colors"
              >
                Login
              </Link>
              <Link
                to="/register"
                className="bg-indigo-600 text-white px-6 py-2 rounded-md hover:bg-indigo-700 transition-all duration-200 font-medium shadow-sm hover:shadow-md"
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <main className="flex-grow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24 text-center">
          <p className="text-sm uppercase tracking-widest text-indigo-600 font-semibold mb-6">Goals. Deliverables. Execution.</p>
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            The operating system for firms that ship complex work on time
          </h1>
          <p className="text-lg md:text-xl text-gray-600 mb-12 max-w-3xl mx-auto px-4 leading-relaxed">
            Goalie4 gives operations teams a single command center for client matters, project deliverables,
            recurring obligations, and deep-focus execution. Track the hidden work, predict effort with real data,
            and keep every promise to your clients.
          </p>
          <div className="flex flex-col md:flex-row gap-4 justify-center mb-16">
            <Link
              to="/register"
              className="inline-block bg-indigo-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 md:min-w-[200px]"
            >
              Start Free Today
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-indigo-600 bg-white border border-indigo-100 rounded-lg hover:border-indigo-200 hover:text-indigo-700 transition-all duration-200 shadow"
            >
              Book a Live Walkthrough
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left max-w-4xl mx-auto">
            <div className="bg-white rounded-xl shadow-sm p-6 border border-indigo-50">
              <p className="text-3xl font-bold text-gray-900">4-level structure</p>
              <p className="text-gray-600 mt-2">Matter → Project → Deliverable → Task hierarchy keeps every commitment linked back to the client relationship.</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-6 border border-indigo-50">
              <p className="text-3xl font-bold text-gray-900">Real-time focus</p>
              <p className="text-gray-600 mt-2">Smart Focus Mode queues the next best task, auto-starts your timer, and blocks distractions until it’s done.</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-6 border border-indigo-50">
              <p className="text-3xl font-bold text-gray-900">Predictive timing</p>
              <p className="text-gray-600 mt-2">Every minute tracked feeds rolling averages, so templates and recurring work ship with data-backed estimates.</p>
            </div>
          </div>
          <Link
            to="/login"
            className="mt-12 inline-flex items-center text-indigo-600 font-semibold hover:text-indigo-700"
          >
            See how teams are using Goalie4 →
          </Link>
        </div>

        <div className="bg-white py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-8">
              Why Choose Goalie4?
            </h2>
            <p className="text-center text-gray-600 text-lg mb-16 max-w-3xl mx-auto">
              We built Goalie4 to be the calm, reliable system-of-record that busy operations teams ask for: structured,
              opinionated where it matters, and flexible everywhere else.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
              <div className="text-center p-6 rounded-lg hover:bg-gray-50 transition-colors duration-200">
                <div className="flex justify-center mb-6">
                  <div className="p-3 bg-indigo-100 rounded-full">
                    <Layers className="h-8 w-8 text-indigo-600" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-4">Template Deliverables</h3>
                <p className="text-gray-600 leading-relaxed">
                  Capture your proven playbooks once, complete with dependencies, SOPs, and time expectations. Kick off
                  new client engagements in minutes—consistency and profitability included.
                </p>
              </div>
              <div className="text-center p-6 rounded-lg hover:bg-gray-50 transition-colors duration-200">
                <div className="flex justify-center mb-6">
                  <div className="p-3 bg-indigo-100 rounded-full">
                    <Clock className="h-8 w-8 text-indigo-600" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-4">Time Tracking & Focus Mode</h3>
                <p className="text-gray-600 leading-relaxed">
                  Glide into deep work with a single click. Goalie4 locks the screen on the current task, auto-runs the timer,
                  and saves notes and context so you can stop and restart without losing flow.
                </p>
              </div>
              <div className="text-center p-6 rounded-lg hover:bg-gray-50 transition-colors duration-200">
                <div className="flex justify-center mb-6">
                  <div className="p-3 bg-indigo-100 rounded-full">
                    <Calendar className="h-8 w-8 text-indigo-600" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-4">Regular & Recurring Tasks</h3>
                <p className="text-gray-600 leading-relaxed">
                  Automate the “don’t forget” work. Daily standups, month-end close, compliance checks—everything regenerates,
                  logs completion history, and forecasts the effort required next time.
                </p>
              </div>
            </div>
            
            {/* Additional Features Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center p-6 rounded-lg hover:bg-gray-50 transition-colors duration-200">
                <div className="flex justify-center mb-6">
                  <div className="p-3 bg-indigo-100 rounded-full">
                    <Zap className="h-8 w-8 text-indigo-600" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-4">Smart Dependencies</h3>
                <p className="text-gray-600 leading-relaxed">
                  Build conditional paths that unlock only when the previous steps are truly done. No more manual checklists
                  or guessing which task is safe to start.
                </p>
              </div>
              
              <div className="text-center p-6 rounded-lg hover:bg-gray-50 transition-colors duration-200">
                <div className="flex justify-center mb-6">
                  <div className="p-3 bg-indigo-100 rounded-full">
                    <Users className="h-8 w-8 text-indigo-600" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-4">Client-grade accountability</h3>
                <p className="text-gray-600 leading-relaxed">
                  Assign ownership, surface blockers instantly, and keep the entire team (and your clients) confident that
                  every deliverable is moving on schedule.
                </p>
              </div>
              
              <div className="text-center p-6 rounded-lg hover:bg-gray-50 transition-colors duration-200">
                <div className="flex justify-center mb-6">
                  <div className="p-3 bg-indigo-100 rounded-full">
                    <BarChart className="h-8 w-8 text-indigo-600" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-4">Matter-Project Hierarchy</h3>
                <p className="text-gray-600 leading-relaxed">
                  See the entire book of work in a single glance. Drill from the client-level mandate down to the exact task
                  someone is doing right now—complete clarity from strategy to execution.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="py-24 bg-gradient-to-r from-indigo-600 to-indigo-700">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              <div>
                <h3 className="text-sm uppercase tracking-widest text-indigo-200 font-semibold mb-3">Designed for operators</h3>
                <p className="text-3xl font-bold leading-tight">
                  Replace spreadsheets, nagging reminders, and scattered SOP docs with one platform built for delivery teams.
                </p>
              </div>
              <div className="space-y-4">
                <p className="text-indigo-100">• Live dashboards highlighting overdue and at-risk deadlines across every matter.</p>
                <p className="text-indigo-100">• Automatic time predictions for template tasks and recurring work, updated after every completion.</p>
                <p className="text-indigo-100">• Embedded SOPs, documents, and notes on every task so context stays with the work.</p>
              </div>
              <div className="space-y-4">
                <p className="text-indigo-100">• Secure client hierarchy that mirrors the way professional-service firms operate.</p>
                <p className="text-indigo-100">• Focus Mode that locks attention, tracks notes, and persists progress if you need to step away.</p>
                <p className="text-indigo-100">• Clean API-style audit trail through the query validator and time-entry history.</p>
              </div>
            </div>
            <div className="mt-12 flex flex-col md:flex-row gap-4">
              <div className="flex-1 bg-white/10 rounded-xl p-6 backdrop-blur border border-white/10">
                <p className="text-4xl font-bold">93%</p>
                <p className="text-indigo-100 mt-2">of pilot teams reported fewer missed deadlines after their first 60 days with Goalie4.</p>
              </div>
              <div className="flex-1 bg-white/10 rounded-xl p-6 backdrop-blur border border-white/10">
                <p className="text-4xl font-bold">12 hrs</p>
                <p className="text-indigo-100 mt-2">Average weekly time saved by operations managers who migrated their recurring tasks into Goalie4.</p>
              </div>
              <div className="flex-1 bg-white/10 rounded-xl p-6 backdrop-blur border border-white/10">
                <p className="text-4xl font-bold">∞ clarity</p>
                <p className="text-indigo-100 mt-2">Because every task, note, document, and completion history is tied back to the client and deliverable.</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <footer className="bg-gray-900 text-white py-12 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:justify-between items-center space-y-4 md:space-y-0">
            <div className="flex items-center">
              <Target className="h-8 w-8" />
              <span className="ml-2 text-2xl font-bold">Goalie4</span>
            </div>
            <div className="text-gray-400">
              © {new Date().getFullYear()} Barnes Software Limited. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default HomePage;
